import { RumEvent } from "@datadog/browser-rum";

// Pure PII-scrubbing helpers used by Datadog RUM `beforeSend`. Kept free of any
// SDK side effects so they can be unit-tested in isolation. The goal: never let
// query strings, identifiers, emails, or other PII leave the browser in URLs,
// referrers, resource URLs, or error text.

const EMAIL_REGEX = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const NUMERIC_ID_REGEX = /^\d+$/;
const LONG_TOKEN_REGEX = /^[A-Za-z0-9_-]{20,}$/;

export const REDACTED_EMAIL = "[REDACTED_EMAIL]";

const splitOriginAndPath = (url: string): [string, string] => {
  const schemeMatch = url.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//);

  if (!schemeMatch) {
    return ["", url];
  }

  const afterScheme = url.indexOf("/", schemeMatch[0].length);

  if (afterScheme === -1) {
    return [url, ""];
  }

  return [url.slice(0, afterScheme), url.slice(afterScheme)];
};

/**
 * Removes the query string and hash fragment from a URL while preserving the
 * origin + path. Deep-link query params can carry document ids, fund ids,
 * tokens, and search terms, so they are dropped entirely.
 */
export const stripQueryString = (url: string | undefined): string | undefined => {
  if (!url) return url;

  const queryIndex = url.search(/[?#]/);

  return queryIndex === -1 ? url : url.slice(0, queryIndex);
};

/**
 * Replaces dynamic path segments (numeric ids, UUIDs, long opaque tokens) with
 * stable placeholders so URLs group cleanly in Datadog and leak no identifiers.
 * Operates on a full URL or a bare path.
 */
export const redactSensitiveSegments = (url: string | undefined): string | undefined => {
  if (!url) return url;

  const [origin, path] = splitOriginAndPath(url);

  const redactedPath = path
    .split("/")
    .map((segment) => {
      if (!segment) return segment;
      if (UUID_REGEX.test(segment)) return ":uuid";
      if (NUMERIC_ID_REGEX.test(segment)) return ":id";
      if (LONG_TOKEN_REGEX.test(segment)) return ":token";

      return segment;
    })
    .join("/");

  return `${origin}${redactedPath}`;
};

/**
 * Masks email addresses found anywhere in a free-text string (e.g. error
 * messages or stack frames).
 */
export const redactPiiText = (text: string | undefined): string | undefined => {
  if (!text) return text;

  return text.replace(EMAIL_REGEX, REDACTED_EMAIL);
};

/**
 * Applies query-string stripping + segment redaction to a URL value.
 */
export const sanitizeUrl = (url: string | undefined): string | undefined =>
  redactSensitiveSegments(stripQueryString(url));

/**
 * RUM `beforeSend` callback. Mutates the outgoing event in place to remove PII
 * before it is sent to Datadog. Always returns `true` (events are kept, just
 * scrubbed). Defensive against partially-shaped events.
 */
export const beforeSend = (event: RumEvent): boolean => {
  if (!event) return true;

  if (event.view) {
    event.view.url = sanitizeUrl(event.view.url) as string;

    if (event.view.referrer) {
      event.view.referrer = sanitizeUrl(event.view.referrer);
    }
  }

  if (event.type === "resource" && event.resource) {
    event.resource.url = sanitizeUrl(event.resource.url) as string;
  }

  if (event.type === "error" && event.error) {
    event.error.message = redactPiiText(event.error.message) as string;

    if (event.error.stack) {
      event.error.stack = redactPiiText(event.error.stack);
    }
  }

  return true;
};
